import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from '../../lib/pdfWorker';

interface UsePdfPageResult {
  width: number;
  height: number;
  rendering: boolean;
}

/**
 * Hook: Render a single PDF page to a canvas element.
 * Handles DPR scaling for retina, render cancellation on change/unmount.
 */
export function usePdfPage(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  document: PDFDocumentProxy | null,
  pageNumber: number,
  scale: number,
  isVisible: boolean,
): UsePdfPageResult {
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [rendering, setRendering] = useState(false);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);

  useEffect(() => {
    if (!document || !canvasRef.current || !isVisible || pageNumber < 1 || pageNumber > document.numPages) return;

    let cancelled = false;

    (async () => {
      try {
        // Cancel any pending render
        renderTaskRef.current?.cancel();

        const page = await document.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        const cssWidth = viewport.width;
        const cssHeight = viewport.height;
        setDims({ width: cssWidth, height: cssHeight });

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(cssWidth * dpr);
        canvas.height = Math.floor(cssHeight * dpr);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;

        const ctx = canvas.getContext('2d');
        if (!ctx || cancelled) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        setRendering(true);
        const renderTask = page.render({ canvasContext: ctx, viewport, canvas } as any);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        if (!cancelled) setRendering(false);
      } catch (err) {
        // RenderTask.cancel() throws — that's expected
        if (!cancelled && (err as Error)?.message !== 'Rendering cancelled') {
          console.warn('[usePdfPage] render error:', err);
          setRendering(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [document, pageNumber, scale, isVisible, canvasRef]);

  return { ...dims, rendering };
}
